import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { useNavigate } from '@tanstack/react-router';
import { useOrgStore, type OrgArea, type DmPreview } from '@/stores/org.store';
import { useAuthStore } from '@/stores/auth.store';
import { AreaIcon } from '@/components/ui/icon-picker';
import { searchMessages } from '@/lib/api';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgSlug: string | undefined;
}

interface PaletteItem {
  id: string;
  label: string;
  group: 'Rooms' | 'DMs' | 'Pages' | 'Messages';
  icon: React.ReactNode;
  /** Rendered instead of `label` when the row needs markup (match highlights). */
  labelNode?: React.ReactNode;
  /** Second line, used by message hits to show where the match lives. */
  sublabel?: string;
  onSelect: () => void;
}

interface MessageHit {
  message: {
    id: string;
    content: string;
    author: { userId: string; displayName: string; profilePictureUrl: string | null };
    messageType: string;
    createdAt: string;
  };
  area: { id: string; name: string };
  org: { id: string; name: string; color: string | null };
  thread: { id: string; title: string } | null;
}

const NO_HITS: MessageHit[] = [];

// Static page definitions — icons are inline SVGs matching the sidebar
const PAGE_ICON_CLASS = 'h-4 w-4 text-gray-400 dark:text-gray-500';

function InboxIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 005.586 13H4" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function BookmarkIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
    </svg>
  );
}

function ScheduledIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function MembersIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-1.076M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
    </svg>
  );
}

function GroupsIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
    </svg>
  );
}

function AdminIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function SecurityIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function DraftsIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
    </svg>
  );
}

/**
 * `ts_headline` wraps matches in <mark>. Render those as real elements rather
 * than injecting server HTML — a palette row never needs innerHTML. Any other
 * tags in the snippet are stripped, matching the search page's sanitiser.
 */
function renderHeadline(text: string) {
  return text
    .split(/(<mark>[\s\S]*?<\/mark>)/g)
    .filter(Boolean)
    .map((part, i) => {
      const match = /^<mark>([\s\S]*?)<\/mark>$/.exec(part);
      return match ? (
        <mark key={i} className="rounded bg-yellow-200 px-0.5 dark:bg-yellow-800 dark:text-yellow-100">
          {match[1].replace(/<[^>]*>/g, '')}
        </mark>
      ) : (
        <span key={i}>{part.replace(/<[^>]*>/g, '')}</span>
      );
    });
}

function MessageIcon() {
  return (
    <svg className={PAGE_ICON_CLASS} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h8M8 8h8m-8 8h4m5 5l-3-3H6a2 2 0 01-2-2V6a2 2 0 012-2h12a2 2 0 012 2v8a2 2 0 01-2 2h-1v5z" />
    </svg>
  );
}

function DmAvatarSmall({ participant }: { participant: { displayName: string; profilePictureUrl: string | null } }) {
  if (participant.profilePictureUrl) {
    return (
      <img
        src={participant.profilePictureUrl}
        alt={participant.displayName}
        className="h-5 w-5 rounded-full object-cover"
      />
    );
  }
  return (
    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-100 text-[10px] font-medium text-blue-700 dark:bg-blue-900 dark:text-blue-300">
      {participant.displayName.charAt(0).toUpperCase()}
    </span>
  );
}

export function CommandPalette({ open, onOpenChange, orgSlug }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const areas = useOrgStore((s) => s.areas);
  const dms = useOrgStore((s) => s.dms);
  const orgIdBySlug = useOrgStore((s) => s.orgIdBySlug);
  const areaSlugById = useOrgStore((s) => s.areaSlugById);
  const user = useAuthStore((s) => s.user);
  const orgs = useAuthStore((s) => s.orgs);

  // Message hits come from the server, so unlike the local jump targets they
  // arrive after a round trip.
  const [messageHits, setMessageHits] = useState<MessageHit[]>(NO_HITS);
  const [searching, setSearching] = useState(false);
  // A slow response for an abandoned query must never overwrite the results
  // for the query the user is actually looking at.
  const searchSeq = useRef(0);
  const currentOrgId = orgSlug ? orgIdBySlug[orgSlug] : undefined;

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 2) {
      setMessageHits(NO_HITS);
      setSearching(false);
      return;
    }
    const mine = ++searchSeq.current;
    setSearching(true);
    const timer = setTimeout(() => {
      searchMessages({ q, orgId: currentOrgId, limit: 5 })
        .then((res) => {
          if (mine !== searchSeq.current) return;
          setMessageHits(res.results);
        })
        .catch(() => {
          if (mine !== searchSeq.current) return;
          setMessageHits(NO_HITS);
        })
        .finally(() => {
          if (mine === searchSeq.current) setSearching(false);
        });
    }, 200);
    return () => clearTimeout(timer);
  }, [query, open, currentOrgId]);

  const currentOrg = orgs.find((o) => o.slug === orgSlug);
  const isAdmin = currentOrg && (currentOrg.role === 'owner' || currentOrg.role === 'admin');
  const isGuest = user?.isGuest === true;

  // Build the list of all palette items
  const allItems = useMemo((): PaletteItem[] => {
    if (!orgSlug) return [];

    const items: PaletteItem[] = [];

    // Areas (member areas, non-archived, non-DM)
    const memberAreas = areas.filter((a) => a.isMember && !a.isArchived && !a.isDm);
    for (const area of memberAreas) {
      items.push({
        id: `area-${area.id}`,
        label: area.name,
        group: 'Rooms',
        icon: <AreaIcon icon={area.icon} iconColor={area.iconColor} className="h-4 w-4" />,
        onSelect: () => {
          navigate({ to: '/org/$orgSlug/$areaSlug', params: { orgSlug, areaSlug: area.slug } });
        },
      });
    }

    // DMs
    if (!isGuest) {
      for (const dm of dms) {
        const otherParticipants = dm.participants.filter((p) => p.userId !== user?.id);
        const dmDisplayName = otherParticipants.map((p) => p.displayName).join(', ') || dm.name;
        const firstOther = otherParticipants[0] ?? dm.participants[0];
        items.push({
          id: `dm-${dm.id}`,
          label: dmDisplayName,
          group: 'DMs',
          icon: firstOther ? <DmAvatarSmall participant={firstOther} /> : <span className="h-4 w-4" />,
          onSelect: () => {
            navigate({ to: '/org/$orgSlug/$areaSlug', params: { orgSlug, areaSlug: dm.id } });
          },
        });
      }
    }

    // Pages (scoped for guests)
    if (!isGuest) {
      items.push({
        id: 'page-inbox',
        label: 'Inbox',
        group: 'Pages',
        icon: <InboxIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/inbox', params: { orgSlug } }),
      });
      items.push({
        id: 'page-search',
        label: 'Search',
        group: 'Pages',
        icon: <SearchIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/search', params: { orgSlug } }),
      });
      items.push({
        id: 'page-bookmarks',
        label: 'Bookmarks',
        group: 'Pages',
        icon: <BookmarkIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/bookmarks', params: { orgSlug } }),
      });
      items.push({
        id: 'page-drafts',
        label: 'Drafts',
        group: 'Pages',
        icon: <DraftsIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/drafts', params: { orgSlug } }),
      });
      items.push({
        id: 'page-scheduled',
        label: 'Scheduled Messages',
        group: 'Pages',
        icon: <ScheduledIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/scheduled-messages', params: { orgSlug } }),
      });
      items.push({
        id: 'page-members',
        label: 'Members',
        group: 'Pages',
        icon: <MembersIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/members', params: { orgSlug } }),
      });
      if (isAdmin) {
        items.push({
          id: 'page-groups',
          label: 'Groups',
          group: 'Pages',
          icon: <GroupsIcon />,
          onSelect: () => navigate({ to: '/org/$orgSlug/groups', params: { orgSlug } }),
        });
        items.push({
          id: 'page-admin',
          label: 'Admin Dashboard',
          group: 'Pages',
          icon: <AdminIcon />,
          onSelect: () => navigate({ to: '/org/$orgSlug/admin', params: { orgSlug } }),
        });
      }
      items.push({
        id: 'page-security',
        label: 'Security',
        group: 'Pages',
        icon: <SecurityIcon />,
        onSelect: () => navigate({ to: '/org/$orgSlug/security', params: { orgSlug } }),
      });
    }

    return items;
  }, [orgSlug, areas, dms, user, isGuest, isAdmin, navigate]);

  // Filter items by query (case-insensitive substring match)
  const filteredItems = useMemo(() => {
    if (!query.trim()) return allItems;
    const q = query.toLowerCase().trim();
    return allItems.filter((item) => item.label.toLowerCase().includes(q));
  }, [allItems, query]);

  // Message hits are already the answer to the query, so they are not run
  // through the label filter that narrows the local jump targets.
  const messageItems = useMemo((): PaletteItem[] => {
    return messageHits.map((hit) => {
      const hitOrgSlug = orgSlug;
      const hitAreaSlug = areaSlugById[hit.area.id] ?? hit.area.id;
      const where = hit.thread
        ? `#${hit.area.name} › ${hit.thread.title ?? 'comments'}`
        : `#${hit.area.name}`;
      return {
        id: `msg-${hit.message.id}`,
        label: hit.message.content.replace(/<[^>]*>/g, ''),
        labelNode: renderHeadline(hit.message.content),
        sublabel: `${hit.message.author.displayName} in ${where}`,
        group: 'Messages',
        icon: <MessageIcon />,
        onSelect: () => {
          if (!hitOrgSlug) return;
          if (hit.thread) {
            navigate({
              to: '/org/$orgSlug/$areaSlug/thread/$threadId',
              params: { orgSlug: hitOrgSlug, areaSlug: hitAreaSlug, threadId: hit.thread.id },
            });
          } else {
            navigate({
              to: '/org/$orgSlug/$areaSlug',
              params: { orgSlug: hitOrgSlug, areaSlug: hitAreaSlug },
            });
          }
        },
      };
    });
  }, [messageHits, orgSlug, areaSlugById, navigate]);

  // Group filtered items. Messages come LAST on purpose: they arrive
  // asynchronously, and appending them below the instant local matches means
  // a late response never shifts the row the user is about to press Enter on.
  const groupedItems = useMemo(() => {
    const groups: { name: string; items: PaletteItem[] }[] = [];
    const groupOrder: PaletteItem['group'][] = ['Rooms', 'DMs', 'Pages'];
    for (const groupName of groupOrder) {
      const groupItems = filteredItems.filter((item) => item.group === groupName);
      if (groupItems.length > 0) {
        groups.push({ name: groupName, items: groupItems });
      }
    }
    if (messageItems.length > 0) {
      groups.push({ name: 'Messages', items: messageItems });
    }
    return groups;
  }, [filteredItems, messageItems]);

  // Flat list of items for keyboard navigation indexing
  const flatItems = useMemo(() => groupedItems.flatMap((g) => g.items), [groupedItems]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Focus the input after a tick (Radix Dialog needs a frame)
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  // Clamp selected index when results change
  useEffect(() => {
    setSelectedIndex((prev) => Math.min(prev, Math.max(0, flatItems.length - 1)));
  }, [flatItems.length]);

  // Scroll selected item into view
  useEffect(() => {
    const selectedEl = listRef.current?.querySelector(`[data-index="${selectedIndex}"]`);
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (item: PaletteItem) => {
      onOpenChange(false);
      item.onSelect();
    },
    [onOpenChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % Math.max(1, flatItems.length));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + flatItems.length) % Math.max(1, flatItems.length));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatItems[selectedIndex];
        if (item) handleSelect(item);
      }
    },
    [flatItems, selectedIndex, handleSelect],
  );

  // Track cumulative index across groups for data-index attributes
  let cumulativeIndex = 0;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm" />
        <Dialog.Content
          className="fixed left-1/2 top-[20%] z-50 w-[calc(100%-1.5rem)] max-w-lg -translate-x-1/2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl sm:w-full dark:border-gray-700 dark:bg-gray-900"
          onKeyDown={handleKeyDown}
          aria-label="Command palette"
        >
          {/* Visually hidden title for accessibility */}
          <Dialog.Title className="sr-only">Command palette</Dialog.Title>
          <Dialog.Description className="sr-only">
            Search and navigate to areas, messages, and pages
          </Dialog.Description>

          {/* Search input */}
          <div className="flex items-center border-b border-gray-200 px-4 dark:border-gray-700">
            <svg
              className="mr-3 h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-500"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Search areas, messages, pages..."
              className="h-12 w-full bg-transparent text-sm text-gray-900 placeholder-gray-400 outline-none dark:text-gray-100 dark:placeholder-gray-500"
              autoComplete="off"
              spellCheck={false}
            />
            <kbd className="ml-2 hidden flex-shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 sm:inline-block dark:border-gray-700 dark:bg-gray-800 dark:text-gray-500">
              ESC
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-80 overflow-y-auto overscroll-contain p-2" role="listbox">
            {flatItems.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                {searching ? 'Searching messages…' : 'No results found'}
              </div>
            ) : (
              groupedItems.map((group) => (
                <div key={group.name} className="mb-1 last:mb-0">
                  <div className="px-2 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                    {group.name}
                  </div>
                  {group.items.map((item) => {
                    const itemIndex = cumulativeIndex++;
                    const isSelected = itemIndex === selectedIndex;
                    return (
                      <button
                        key={item.id}
                        role="option"
                        aria-selected={isSelected}
                        data-index={itemIndex}
                        className={`flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          isSelected
                            ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800'
                        }`}
                        onClick={() => handleSelect(item)}
                        onMouseEnter={() => setSelectedIndex(itemIndex)}
                      >
                        <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center">
                          {item.icon}
                        </span>
                        <span className="flex min-w-0 flex-1 flex-col">
                          <span className="truncate">{item.labelNode ?? item.label}</span>
                          {item.sublabel && (
                            <span className="truncate text-[11px] text-gray-500 dark:text-gray-400">
                              {item.sublabel}
                            </span>
                          )}
                        </span>
                        {isSelected && (
                          <kbd className="hidden flex-shrink-0 rounded border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] font-medium text-gray-400 sm:inline-block dark:border-gray-600 dark:bg-gray-800 dark:text-gray-500">
                            Enter
                          </kbd>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </div>

          {/* Footer hint */}
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-2 dark:border-gray-700">
            <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-500">
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[10px] font-medium dark:border-gray-700 dark:bg-gray-800">
                  <svg className="inline h-2.5 w-2.5" viewBox="0 0 10 10" fill="currentColor"><path d="M5 2L2 5h6L5 2z" /></svg>
                </kbd>
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[10px] font-medium dark:border-gray-700 dark:bg-gray-800">
                  <svg className="inline h-2.5 w-2.5" viewBox="0 0 10 10" fill="currentColor"><path d="M5 8L2 5h6L5 8z" /></svg>
                </kbd>
                navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[10px] font-medium dark:border-gray-700 dark:bg-gray-800">
                  Enter
                </kbd>
                select
              </span>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
